import type { ReactNode } from 'react';
import { Card, CardContent, Box, Typography, alpha } from '@mui/material';

interface Props {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  /** Accent color (defaults to primary). Accepts any CSS color. */
  color?: string;
  hint?: string;
  /** Render as a premium gradient card with light text. */
  gradient?: boolean;
}

/** KPI card used on dashboards (plain or gradient). */
export function StatCard({ label, value, icon, color = '#1e40af', hint, gradient }: Props) {
  if (gradient) {
    return (
      <Card sx={{ height: '100%', color: '#fff', border: 'none', background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.78)} 100%)`, boxShadow: `0 6px 18px ${alpha(color, 0.3)}` }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, '&:last-child': { pb: 1.75 } }}>
          {icon && (
            <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#ffffff', 0.2), flexShrink: 0, '& svg': { fontSize: 20 } }}>
              {icon}
            </Box>
          )}
          <Box minWidth={0}>
            <Typography sx={{ opacity: 0.85, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }} noWrap>{value}</Typography>
            {hint && <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 11 }}>{hint}</Typography>}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, '&:last-child': { pb: 1.75 } }}>
        {icon && (
          <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color, bgcolor: alpha(color, 0.12), flexShrink: 0, '& svg': { fontSize: 20 } }}>
            {icon}
          </Box>
        )}
        <Box minWidth={0}>
          <Typography color="text.secondary" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</Typography>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }} noWrap>{value}</Typography>
          {hint && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{hint}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}
